"use server";

import { prisma } from "../../lib/prisma";
import { auth } from "../../lib/auth";
import { dbg } from "../../lib/debug-log";
import type { TickerCatchUpItem } from "../../lib/ticker-types";

// Unread catch-up batch for the global ticker (app/components/global-ticker.tsx)
// — oldest first, so they play back in the order they actually happened.
export async function getUnreadTickerEvents(): Promise<TickerCatchUpItem[]> {
  const session = await auth();
  if (!session) return [];

  const rows = await prisma.tickerEvent.findMany({
    where: { userId: session.user.id, readAt: null },
    orderBy: { createdAt: "asc" },
    take: 50,
  });

  return rows.map((r) => {
    let parsed: { icon?: string; text?: string; url?: string } = {};
    try { parsed = JSON.parse(r.payload); } catch { /* malformed row — fall back to blanks below */ }
    return { dbId: r.id, icon: parsed.icon ?? "📰", text: parsed.text ?? "", url: parsed.url };
  });
}

// Batched, fire-once read marker — called right after the client enqueues the
// catch-up batch on mount, not per-item (these are passive ticker scrolls,
// not an inbox the user dismisses one at a time). Scoped to the caller's own
// userId even though ids are server-generated cuids — cheap insurance against
// a forged id list reaching across users.
export async function markTickerEventsRead(ids: string[]): Promise<void> {
  const session = await auth();
  if (!session || ids.length === 0) return;

  await prisma.tickerEvent.updateMany({
    where: { id: { in: ids }, userId: session.user.id },
    data: { readAt: new Date() },
  }).catch((err) => dbg.error("notify", "markTickerEventsRead failed", { userId: session.user.id, error: String(err) }));
}
